import 'react';
import classnames from 'classnames';
import styled, { css } from 'styled-components';
import { selectFromProps, selectFromMap, stylesFromMap, selectFromTheme } from '@thisisbud/sc-utils';
import { kebabCaseToPascalCase } from '@thisisbud/string-utils';
import { jsx, jsxs } from 'react/jsx-runtime';
import { VisuallyHidden } from '@thisisbud/bui-react-visually-hidden';

let _$b = t => t,
  _t$b;
const commonCss = /*#__PURE__*/css(_t$b || (_t$b = _$b`
  max-width: ${0};
  margin: 0;
  text-align: ${0};
  ${0}
`), "38.75rem", selectFromProps('$align'), selectFromMap(selectFromProps('$align'), {
  center: stylesFromMap({
    'margin-left': 'auto',
    // eslint-disable-line @typescript-eslint/naming-convention
    'margin-right': 'auto' // eslint-disable-line @typescript-eslint/naming-convention
  }),

  left: stylesFromMap({
    'margin-left': '0',
    // eslint-disable-line @typescript-eslint/naming-convention
    'margin-right': 'auto' // eslint-disable-line @typescript-eslint/naming-convention
  }),

  right: stylesFromMap({
    'margin-left': 'auto',
    // eslint-disable-line @typescript-eslint/naming-convention
    'margin-right': '0' // eslint-disable-line @typescript-eslint/naming-convention
  })
}));

let _$a = t => t,
  _t$a,
  _t2$a,
  _t3$2,
  _t4,
  _t5;
const paragraphClassName = 'bui-paragraph';

/**
 * Creates styles for a paragraph component with appropriate values for a given size.
 *
 * @param size - A key of the target size
 */
function getParagraphCss(size) {
  const sizeKey = kebabCaseToPascalCase(size);
  return css(_t$a || (_t$a = _$a`
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  letter-spacing: ${0};
  line-height: ${0};
  word-break: break-word;
  white-space: break-spaces;

  strong {
    font-weight: ${0};
  }
`), function (props) {
    const {
      $variant,
      theme
    } = props;
    return $variant === 'off' ? theme.colorTextPrimaryOff : theme.colorTextPrimaryMain;
  }, selectFromTheme(`fontFamilyBody${sizeKey}`), selectFromTheme(`fontSizeBody${sizeKey}Default`), selectFromTheme(`fontWeightBody${sizeKey}`), selectFromTheme(`letterSpacingBody${sizeKey}Default`), selectFromTheme(`lineHeightBody${sizeKey}Default`), selectFromTheme(`fontWeightBody${sizeKey}Strong`));
}
const paragraphModifiers = /*#__PURE__*/css(_t2$a || (_t2$a = _$a`
  * + & {
    margin-top: ${0};
  }

  .bui-btn + & {
    margin-top: ${0};
  }

  .bui-icon-roundel + & {
    margin-top: ${0};
  }

  .bui-entity-list + & {
    margin-top: ${0};
  }
`), selectFromTheme('spacingXXSmallDefault'), selectFromTheme('spacingLargeDefault'), selectFromTheme('spacingXLargeDefault'), selectFromTheme('spacingXXXXLargeDefault'));
const paragraphCss = getParagraphCss('regular');
const paragraphSmallCss = getParagraphCss('small');
const paragraphXSmallCss = getParagraphCss('x-small');

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledParagraph = /*#__PURE__*/styled.p.withConfig({
  displayName: "StyledParagraph",
  componentId: "sc-2kzrbo-0"
})(_t3$2 || (_t3$2 = _$a`
  ${0}
  ${0}
  ${0}
`), commonCss, paragraphCss, paragraphModifiers);

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledParagraphSmall = /*#__PURE__*/styled.p.withConfig({
  displayName: "StyledParagraphSmall",
  componentId: "sc-2kzrbo-1"
})(_t4 || (_t4 = _$a`
  ${0}
  ${0}
  ${0}
`), commonCss, paragraphSmallCss, paragraphModifiers);

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledParagraphXSmall = /*#__PURE__*/styled.p.withConfig({
  displayName: "StyledParagraphXSmall",
  componentId: "sc-2kzrbo-2"
})(_t5 || (_t5 = _$a`
  ${0}
  ${0}
  ${0}
`), commonCss, paragraphXSmallCss, paragraphModifiers);

/**
 * A component that renders a text paragraph.
 * To use bold text wrap the text in a `strong` tag.
 *
 * @remarks By default the component renders as a `p` tag, but any tag or component can be used via the `as` prop
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { Paragraph } from '@thisisbud/bui-react-typography'
 *
 * function ExampleComponent() {
 *   return <Paragraph>The quick <strong>brown fox</strong> jumps over the lazy dog.</Paragraph>
 * }
 * ```
 */
function Paragraph(props) {
  const {
    align,
    as,
    children,
    className,
    size,
    id,
    variant
  } = props;
  let Component; // eslint-disable-line @typescript-eslint/naming-convention

  switch (size) {
    case 'small':
      Component = StyledParagraphSmall;
      break;
    case 'x-small':
      Component = StyledParagraphXSmall;
      break;
    default:
      Component = StyledParagraph;
  }
  return /*#__PURE__*/jsx(Component, {
    $align: align,
    $variant: variant,
    as: as,
    className: classnames(paragraphClassName, className),
    id: id,
    children: children
  });
}
Paragraph.defaultProps = {
  align: 'left',
  size: 'regular',
  variant: 'default'
};
Paragraph.displayName = 'Paragraph';

let _$9 = t => t,
  _t$9,
  _t2$9,
  _t3$1;
const h1ClassName = 'bui-h1';
const h1Css = /*#__PURE__*/css(_t$9 || (_t$9 = _$9`
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  letter-spacing: ${0};
  line-height: ${0};
  text-transform: ${0};
  word-break: break-word;
  white-space: break-spaces;
`), selectFromTheme('colorTextPrimaryMain'), selectFromTheme('fontFamilyHeading1'), selectFromTheme('fontSizeHeading1Default'), selectFromTheme('fontWeightHeading1'), selectFromTheme('letterSpacingHeading1Default'), selectFromTheme('lineHeightHeading1Default'), selectFromTheme('textTransformHeading1'));
const h1Modifiers = /*#__PURE__*/css(_t2$9 || (_t2$9 = _$9`
  * + & {
    margin-top: ${0};
  }

  .bui-icon-roundel + & {
    margin-top: ${0};
  }
`), selectFromTheme('spacingXXSmallDefault'), selectFromTheme('spacingXLargeDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledH1 = /*#__PURE__*/styled.h1.withConfig({
  displayName: "StyledH1",
  componentId: "sc-yakodi-0"
})(_t3$1 || (_t3$1 = _$9`
  ${0}
  ${0}
  ${0}
`), commonCss, h1Css, h1Modifiers);

/**
 * A component that renders a level 1 text heading.
 *
 * @remarks By default the component renders as a `h1` tag, but any tag or component can be used via the `as` prop
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { H1 } from '@thisisbud/bui-react-typography'
 *
 * function ExampleComponent() {
 *   return <H1>The quick brown fox jumps over the lazy dog</H1>
 * }
 * ```
 */
function H1(props) {
  const {
    align,
    as,
    children,
    className,
    id
  } = props;
  return /*#__PURE__*/jsx(StyledH1, {
    $align: align,
    as: as,
    className: classnames(h1ClassName, className),
    id: id,
    children: children
  });
}
H1.defaultProps = {
  align: 'left'
};
H1.displayName = 'H1';

let _$8 = t => t,
  _t$8,
  _t2$8,
  _t3;
const h2ClassName = 'bui-h2';
const h2Css = /*#__PURE__*/css(_t$8 || (_t$8 = _$8`
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  letter-spacing: ${0};
  line-height: ${0};
  text-transform: ${0};
  word-break: break-word;
  white-space: break-spaces;
`), selectFromTheme('colorTextPrimaryMain'), selectFromTheme('fontFamilyHeading2'), selectFromTheme('fontSizeHeading2Default'), selectFromTheme('fontWeightHeading2'), selectFromTheme('letterSpacingHeading2Default'), selectFromTheme('lineHeightHeading2Default'), selectFromTheme('textTransformHeading2'));
const h2Modifiers = /*#__PURE__*/css(_t2$8 || (_t2$8 = _$8`
  * + & {
    margin-top: ${0};
  }

  .bui-icon-roundel + & {
    margin-top: ${0};
  }
`), selectFromTheme('spacingXXSmallDefault'), selectFromTheme('spacingXLargeDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledH2 = /*#__PURE__*/styled.h2.withConfig({
  displayName: "StyledH2",
  componentId: "sc-6qasuf-0"
})(_t3 || (_t3 = _$8`
  ${0}
  ${0}
  ${0}
`), commonCss, h2Css, h2Modifiers);

/**
 * A component that renders a level 2 text heading.
 *
 * @remarks By default the component renders as a `h2` tag, but any tag or component can be used via the `as` prop
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { H2 } from '@thisisbud/bui-react-typography'
 *
 * function ExampleComponent() {
 *   return <H2>The quick brown fox jumps over the lazy dog</H2>
 * }
 * ```
 */
function H2(props) {
  const {
    align,
    as,
    children,
    className,
    id
  } = props;
  return /*#__PURE__*/jsx(StyledH2, {
    $align: align,
    as: as,
    className: classnames(h2ClassName, className),
    id: id,
    children: children
  });
}
H2.defaultProps = {
  align: 'left'
};
H2.displayName = 'H2';

let _$7 = t => t,
  _t$7,
  _t2$7;
const h3ClassName = 'bui-h3';
const h3Css = /*#__PURE__*/css(_t$7 || (_t$7 = _$7`
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  letter-spacing: ${0};
  line-height: ${0};
  text-transform: ${0};
  word-break: break-word;
  white-space: break-spaces;

  .bui-text-btn + &,
  .${0} + & {
    margin-top: ${0};
  }

  .bui-table + &,
  .bui-icon-roundel + & {
    margin-top: ${0};
  }
`), selectFromTheme('colorTextPrimaryMain'), selectFromTheme('fontFamilyHeading3'), selectFromTheme('fontSizeHeading3Default'), selectFromTheme('fontWeightHeading3'), selectFromTheme('letterSpacingHeading3Default'), selectFromTheme('lineHeightHeading3Default'), selectFromTheme('textTransformHeading3'), h1ClassName, selectFromTheme('spacingLargeDefault'), selectFromTheme('spacingXLargeDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledH3 = /*#__PURE__*/styled.h3.withConfig({
  displayName: "StyledH3",
  componentId: "sc-snpaa-0"
})(_t2$7 || (_t2$7 = _$7`
  ${0}
  ${0}
`), commonCss, h3Css);

/**
 * A component that renders a level 3 text heading.
 *
 * @remarks By default the component renders as a `h3` tag, but any tag or component can be used via the `as` prop
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { H3 } from '@thisisbud/bui-react-typography'
 *
 * function ExampleComponent() {
 *   return <H3>The quick brown fox jumps over the lazy dog</H3>
 * }
 * ```
 */
function H3(props) {
  const {
    align,
    as,
    children,
    className,
    id
  } = props;
  return /*#__PURE__*/jsx(StyledH3, {
    $align: align,
    as: as,
    className: classnames(h3ClassName, className),
    id: id,
    children: children
  });
}
H3.defaultProps = {
  align: 'left'
};
H3.displayName = 'H3';

let _$6 = t => t,
  _t$6,
  _t2$6;
const h4ClassName = 'bui-h4';
const h4Css = /*#__PURE__*/css(_t$6 || (_t$6 = _$6`
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  letter-spacing: ${0};
  line-height: ${0};
  text-transform: ${0};
  word-break: break-word;
  white-space: break-spaces;

  * + & {
    margin-top: ${0};
  }

  .bui-icon-roundel + & {
    margin-top: ${0};
  }
`), selectFromTheme('colorTextPrimaryMain'), selectFromTheme('fontFamilyHeading4'), selectFromTheme('fontSizeHeading4Default'), selectFromTheme('fontWeightHeading4'), selectFromTheme('letterSpacingHeading4Default'), selectFromTheme('lineHeightHeading4Default'), selectFromTheme('textTransformHeading4'), selectFromTheme('spacingLargeDefault'), selectFromTheme('spacingXLargeDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledH4 = /*#__PURE__*/styled.h4.withConfig({
  displayName: "StyledH4",
  componentId: "sc-139jepx-0"
})(_t2$6 || (_t2$6 = _$6`
  ${0}
  ${0}
`), commonCss, h4Css);

/**
 * A component that renders a level 4 text heading.
 *
 * @remarks By default the component renders as a `h4` tag, but any tag or component can be used via the `as` prop
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { H4 } from '@thisisbud/bui-react-typography'
 *
 * function ExampleComponent() {
 *   return <H4>The quick brown fox jumps over the lazy dog</H4>
 * }
 * ```
 */
function H4(props) {
  const {
    align,
    as,
    children,
    className,
    id
  } = props;
  return /*#__PURE__*/jsx(StyledH4, {
    $align: align,
    as: as,
    className: classnames(h4ClassName, className),
    id: id,
    children: children
  });
}
H4.defaultProps = {
  align: 'left'
};
H4.displayName = 'H4';

let _$5 = t => t,
  _t$5,
  _t2$5;
const h5ClassName = 'bui-h5';
const h5Css = /*#__PURE__*/css(_t$5 || (_t$5 = _$5`
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  letter-spacing: ${0};
  line-height: ${0};
  text-transform: ${0};
  word-break: break-word;
  white-space: break-spaces;

  .bui-icon-roundel + & {
    margin-top: ${0};
  }
`), selectFromTheme('colorTextPrimaryOff'), selectFromTheme('fontFamilyHeading5'), selectFromTheme('fontSizeHeading5Default'), selectFromTheme('fontWeightHeading5'), selectFromTheme('letterSpacingHeading5Default'), selectFromTheme('lineHeightHeading5Default'), selectFromTheme('textTransformHeading5'), selectFromTheme('spacingXLargeDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledH5 = /*#__PURE__*/styled.h5.withConfig({
  displayName: "StyledH5",
  componentId: "sc-2f8cmc-0"
})(_t2$5 || (_t2$5 = _$5`
  ${0}
  ${0}
`), commonCss, h5Css);

/**
 * A component that renders a level 5 text heading.
 *
 * @remarks By default the component renders as a `h5` tag, but any tag or component can be used via the `as` prop
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { H5 } from '@thisisbud/bui-react-typography'
 *
 * function ExampleComponent() {
 *   return <H5>The quick brown fox jumps over the lazy dog</H5>
 * }
 * ```
 */
function H5(props) {
  const {
    align,
    as,
    children,
    className,
    id
  } = props;
  return /*#__PURE__*/jsx(StyledH5, {
    $align: align,
    as: as,
    className: classnames(h5ClassName, className),
    id: id,
    children: children
  });
}
H5.defaultProps = {
  align: 'left'
};
H5.displayName = 'H5';

let _$4 = t => t,
  _t$4,
  _t2$4;
const inputLabelClassName = 'bui-input-label';
const inputLabelCss = /*#__PURE__*/css(_t$4 || (_t$4 = _$4`
  margin: 0;
  ${0}
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  line-height: ${0};
  word-break: break-word;
  white-space: break-spaces;
`), selectFromMap(selectFromProps('$disabled'), {
  false: stylesFromMap({
    color: selectFromTheme('colorTextPrimaryMain')
  }),
  true: stylesFromMap({
    color: selectFromTheme('colorTextPrimaryOff')
  })
}), selectFromTheme('fontFamilyBodyRegular'), selectFromTheme('fontSizeBodyRegularDefault'), selectFromTheme('fontWeightBodyRegular'), selectFromTheme('lineHeightBodyRegularDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledInputLabel = /*#__PURE__*/styled.label.withConfig({
  displayName: "StyledInputLabel",
  componentId: "sc-1euykh5-0"
})(_t2$4 || (_t2$4 = _$4`
  ${0}
`), inputLabelCss);

/**
 * A component that renders a label for an input.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { InputLabel } from '@thisisbud/bui-react-typography'
 *
 * function Example() {
 *   return <InputLabel id='input-label'>Username</InputLabel>
 * }
 * ```
 */
function InputLabel(props) {
  const {
    children,
    className,
    disabled,
    htmlFor
  } = props;
  return /*#__PURE__*/jsx(StyledInputLabel, {
    $disabled: disabled,
    className: classnames(inputLabelClassName, className),
    htmlFor: htmlFor,
    children: children
  });
}
InputLabel.defaultProps = {
  disabled: false
};
InputLabel.displayName = 'InputLabel';

let _$3 = t => t,
  _t$3,
  _t2$3;
const fieldsetLegendClassName = 'bui-fieldset-legend';
const fieldsetLegendCss = /*#__PURE__*/css(_t$3 || (_t$3 = _$3`
  display: block;
  padding: 0;
  margin: 0;
  ${0}
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  line-height: ${0};
  word-break: break-word;
  white-space: break-spaces;
`), selectFromMap(selectFromProps('$disabled'), {
  false: stylesFromMap({
    color: selectFromTheme('colorTextPrimaryMain')
  }),
  true: stylesFromMap({
    color: selectFromTheme('colorTextPrimaryOff')
  })
}), selectFromTheme('fontFamilyBodyRegular'), selectFromTheme('fontSizeBodyRegularDefault'), selectFromTheme('fontWeightBodyRegular'), selectFromTheme('lineHeightBodyRegularDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledLegend = /*#__PURE__*/styled.legend.withConfig({
  displayName: "StyledLegend",
  componentId: "sc-1mihuyk-0"
})(_t2$3 || (_t2$3 = _$3`
  ${0}
`), fieldsetLegendCss);

/**
 * A component that renders a legend for an input fieldset.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { FieldsetLegend } from '@thisisbud/bui-react-typography'
 *
 * function Example() {
 *   return <FieldsetLegend>Address</FieldsetLegend>
 * }
 * ```
 */
function FieldsetLegend(props) {
  const {
    children,
    className,
    disabled,
    id
  } = props;
  return /*#__PURE__*/jsx("div", {
    className: classnames(fieldsetLegendClassName, className),
    children: /*#__PURE__*/jsx(StyledLegend, {
      $disabled: disabled,
      id: id,
      children: children
    })
  });
}
FieldsetLegend.defaultProps = {
  disabled: false
};
FieldsetLegend.displayName = 'FieldsetLegend';

let _$2 = t => t,
  _t$2,
  _t2$2;
const inputHintClassName = 'bui-input-hint';
const inputHintCss = /*#__PURE__*/css(_t$2 || (_t$2 = _$2`
  display: block;
  margin: 0;
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  line-height: ${0};
  word-break: break-word;
  white-space: break-spaces;

  .${0} + &,
  .${0} + & {
    margin-top: ${0};
  }
`), selectFromTheme('colorTextPrimaryOff'), selectFromTheme('fontFamilyBodyRegular'), selectFromTheme('fontSizeBodyRegularDefault'), selectFromTheme('fontWeightBodyRegular'), selectFromTheme('lineHeightBodyRegularDefault'), inputLabelClassName, fieldsetLegendClassName, selectFromTheme('spacingXXXXSmallDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledInputHint = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledInputHint",
  componentId: "sc-t3jxpj-0"
})(_t2$2 || (_t2$2 = _$2`
  ${0}
`), inputHintCss);

/**
 * A component that renders an hint message for inputs.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { InputHint } from '@thisisbud/bui-react-typography'
 *
 * function Example() {
 *   return <InputHint id='input-hint-msg'>You’ll find this on your bank statement</InputHint>
 * }
 * ```
 */
function InputHint(props) {
  const {
    children,
    className,
    hintLabel,
    id
  } = props;
  return /*#__PURE__*/jsxs(StyledInputHint, {
    className: classnames(inputHintClassName, className),
    id: id,
    children: [typeof hintLabel === 'string' ? /*#__PURE__*/jsxs(VisuallyHidden, {
      children: [hintLabel, ":"]
    }) : null, children]
  });
}
InputHint.displayName = 'InputHint';

let _$1 = t => t,
  _t$1,
  _t2$1;
const inputErrorClassName = 'bui-input-error';
const inputErrorCss = /*#__PURE__*/css(_t$1 || (_t$1 = _$1`
  display: block;
  margin: 0;
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  line-height: ${0};
  word-break: break-word;
  white-space: break-spaces;

  .bui-file-name + &,
  .${0} + &,
  .${0} + &,
  .${0} + & {
    margin-top: ${0};
  }
`), selectFromTheme('colorTextPrimaryError'), selectFromTheme('fontFamilyBodyRegular'), selectFromTheme('fontSizeBodyRegularDefault'), selectFromTheme('fontWeightBodyRegularStrong'), selectFromTheme('lineHeightBodyRegularDefault'), inputHintClassName, inputLabelClassName, fieldsetLegendClassName, selectFromTheme('spacingXXXXSmallDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledInputError = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledInputError",
  componentId: "sc-1ek0t5-0"
})(_t2$1 || (_t2$1 = _$1`
  ${0}
`), inputErrorCss);

/**
 * A component that renders an error message for inputs.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { InputError } from '@thisisbud/bui-react-typography'
 *
 * function Example() {
 *   return <InputError id='input-error-msg'>The value provided is not valid</InputError>
 * }
 * ```
 */
function InputError(props) {
  const {
    children,
    className,
    errorLabel,
    id
  } = props;
  return /*#__PURE__*/jsxs(StyledInputError, {
    className: classnames(inputErrorClassName, className),
    id: id,
    children: [typeof errorLabel === 'string' ? /*#__PURE__*/jsxs(VisuallyHidden, {
      children: [errorLabel, ":"]
    }) : null, children]
  });
}
InputError.displayName = 'InputError';

let _ = t => t,
  _t,
  _t2;
const inputWarningClassName = 'bui-input-warning';
const inputWarningCss = /*#__PURE__*/css(_t || (_t = _`
  display: block;
  margin: 0;
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  line-height: ${0};
  word-break: break-word;
  white-space: break-spaces;

  .bui-file-name + &,
  .${0} + &,
  .${0} + &,
  .${0} + & {
    margin-top: ${0};
  }
`), selectFromTheme('colorTextPrimaryWarning'), selectFromTheme('fontFamilyBodyRegular'), selectFromTheme('fontSizeBodyRegularDefault'), selectFromTheme('fontWeightBodyRegularStrong'), selectFromTheme('lineHeightBodyRegularDefault'), inputHintClassName, inputLabelClassName, fieldsetLegendClassName, selectFromTheme('spacingXXXXSmallDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledInputWarning = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledInputWarning",
  componentId: "sc-v2f06q-0"
})(_t2 || (_t2 = _`
  ${0}
`), inputWarningCss);

/**
 * A component that renders a warning message for inputs.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { InputWarning } from '@thisisbud/bui-react-typography'
 *
 * function Example() {
 *   return <InputWarning id='input-warning-msg'>The file already exists</InputWarning>
 * }
 * ```
 */
function InputWarning(props) {
  const {
    children,
    className,
    id,
    warningLabel
  } = props;
  return /*#__PURE__*/jsxs(StyledInputWarning, {
    className: classnames(inputWarningClassName, className),
    id: id,
    children: [typeof warningLabel === 'string' ? /*#__PURE__*/jsxs(VisuallyHidden, {
      children: [warningLabel, ":"]
    }) : null, children]
  });
}
InputWarning.displayName = 'InputWarning';

export { FieldsetLegend, H1, H2, H3, H4, H5, InputError, InputHint, InputLabel, InputWarning, Paragraph, fieldsetLegendClassName, fieldsetLegendCss, h1ClassName, h1Css, h2ClassName, h2Css, h3ClassName, h3Css, h4ClassName, h4Css, h5ClassName, h5Css, inputErrorClassName, inputErrorCss, inputHintClassName, inputHintCss, inputLabelClassName, inputLabelCss, inputWarningClassName, inputWarningCss, paragraphClassName, paragraphCss, paragraphSmallCss, paragraphXSmallCss };
